import React, { useState } from "react";

const giftFields = [
  { name: "name", label: "Your name", type: "text", required: true, autoComplete: "name" },
  { name: "email", label: "Work email", type: "email", required: true, autoComplete: "email" },
  { name: "organization", label: "Company or organization", type: "text", autoComplete: "organization" },
  {
    name: "giftCount",
    label: "Approximate number of gifts",
    type: "number",
    required: true,
    min: "1",
  },
  {
    name: "budget",
    label: "Budget per gift",
    type: "select",
    required: true,
    options: ["Under $75", "$75–$125", "$126–$200", "$200+", "Still deciding"],
  },
  { name: "deliveryDate", label: "Preferred delivery date", type: "date" },
  {
    name: "message",
    label: "Tell us about the recipients and occasion",
    type: "textarea",
    required: true,
    wide: true,
  },
];

const wholesaleFields = [
  { name: "name", label: "Contact name", type: "text", required: true, autoComplete: "name" },
  { name: "email", label: "Business email", type: "email", required: true, autoComplete: "email" },
  {
    name: "businessName",
    label: "Business name",
    type: "text",
    required: true,
    autoComplete: "organization",
  },
  { name: "website", label: "Website or social profile", type: "url", placeholder: "https://" },
  {
    name: "businessType",
    label: "Business type",
    type: "select",
    required: true,
    options: ["Specialty retail", "Restaurant or hospitality", "Corporate pantry", "Distributor", "Other"],
  },
  {
    name: "location",
    label: "City and state",
    type: "text",
    required: true,
    autoComplete: "address-level2",
  },
  {
    name: "monthlyVolume",
    label: "Estimated monthly order",
    type: "select",
    required: true,
    options: ["Under $1,000", "$1,000–$2,500", "$2,501–$5,000", "$5,000+", "Not sure yet"],
  },
  {
    name: "message",
    label: "What would you like to bring to your customers?",
    type: "textarea",
    required: true,
    wide: true,
  },
];

const createInitialValues = (fields) =>
  fields.reduce((values, field) => ({ ...values, [field.name]: "" }), {});

function CommercialInquiryForm({ type }) {
  const isWholesale = type === "wholesale";
  const fields = isWholesale ? wholesaleFields : giftFields;
  const [values, setValues] = useState(() => createInitialValues(fields));
  const [confirmed, setConfirmed] = useState(false);
  const [permission, setPermission] = useState(false);
  const prefix = isWholesale ? "wholesale" : "gifts";

  const updateValue = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    if (confirmed) setConfirmed(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setConfirmed(true);
  };

  return (
    <form className="commercial-form" onSubmit={handleSubmit}>
      <div className="commercial-form__notice">
        <strong>Front-end preview</strong>
        <p>
          This form demonstrates the inquiry experience. Nothing is transmitted
          or stored, and no request is sent to the {isWholesale ? "wholesale" : "gifting"} team.
        </p>
      </div>

      <div className="commercial-form__grid">
        {fields.map((field) => {
          const fieldId = `${prefix}-${field.name}`;

          return (
            <div
              className={`commercial-form__field${field.wide ? " commercial-form__field--wide" : ""}`}
              key={field.name}
            >
              <label htmlFor={fieldId}>
                {field.label}
                {!field.required && <span> Optional</span>}
              </label>

              {field.type === "select" ? (
                <select
                  id={fieldId}
                  name={field.name}
                  value={values[field.name]}
                  onChange={updateValue}
                  required={field.required}
                >
                  <option value="">Choose one</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  id={fieldId}
                  name={field.name}
                  value={values[field.name]}
                  onChange={updateValue}
                  required={field.required}
                  rows="6"
                />
              ) : (
                <input
                  id={fieldId}
                  name={field.name}
                  type={field.type}
                  value={values[field.name]}
                  onChange={updateValue}
                  required={field.required}
                  autoComplete={field.autoComplete}
                  placeholder={field.placeholder}
                  min={field.min}
                />
              )}
            </div>
          );
        })}
      </div>

      <label className="commercial-form__permission" htmlFor={`${prefix}-permission`}>
        <input
          id={`${prefix}-permission`}
          type="checkbox"
          checked={permission}
          onChange={(event) => {
            setPermission(event.target.checked);
            if (confirmed) setConfirmed(false);
          }}
          required
        />
        <span>
          I am happy to be contacted about this {isWholesale ? "trade" : "gift"} inquiry.
        </span>
      </label>

      <div className="commercial-form__footer">
        <button className="button button--primary" type="submit">
          {isWholesale ? "Preview application" : "Preview inquiry"}
        </button>
        <p>Required fields help demonstrate a realistic handoff to a future secure form service.</p>
      </div>

      {confirmed && (
        <div className="commercial-form__confirmation" role="status" aria-live="polite">
          <strong>Preview complete, {values.name || "thank you"}.</strong>
          <p>
            Your entries passed the browser&apos;s form checks. This demo did not
            send or save your information.
          </p>
        </div>
      )}
    </form>
  );
}

export default CommercialInquiryForm;
